/**
 * ReceiptTemplateAdminPage — STAS edits the simplified receipt template
 * that clients see after the office approves their payment.
 *
 * Controls header_ar/en, subheader, license/VAT numbers, contact info,
 * footer text, legal note, the PAID stamp text, and whether the office
 * logo appears at the top.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, AlertCircle, CheckCircle2, Eye } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function ReceiptTemplateAdminPage() {
  const { user } = useAuth();
  const { theme, dir } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [err, setErr] = useState(""); const [okMsg, setOkMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;
  const css = makeStyles(theme);

  useEffect(() => {
    if (!user?.role?.is_root) return;
    Promise.all([
      api.get("/admin/receipt-template"),
      api.get("/admin/portal/disclaimer"),
    ]).then(([t, d]) => { setForm(t.data); setDisclaimer(d.data); })
      .catch((e) => setErr(formatApiError(e)));
  }, [user]);

  if (!user?.role?.is_root) {
    return <div style={css.page}><div style={css.err}>هذه الصفحة لـ STAS فقط</div></div>;
  }
  if (!form || !disclaimer) return <div style={css.page}><div style={css.muted}>…</div></div>;

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(""); setOkMsg("");
    try {
      const [t, d] = await Promise.all([
        api.patch("/admin/receipt-template", form),
        api.patch("/admin/portal/disclaimer", disclaimer),
      ]);
      setForm(t.data); setDisclaimer(d.data);
      setOkMsg("تم الحفظ");
      setTimeout(() => setOkMsg(""), 2500);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setDisc = (k, v) => setDisclaimer({ ...disclaimer, [k]: v });

  return (
    <div dir={dir} style={css.page} data-testid="receipt-template-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="rt-back">
          <BackIcon size={14} /> رجوع
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>قالب الإيصال — للعميل بعد اعتماد السداد</h1>
          <p style={css.sub}>كل ما يظهر للعميل في الإيصال المبسّط (Receipt) القابل للطباعة والتنزيل PDF.</p>
        </div>
      </header>

      {err && <div style={css.err} data-testid="rt-err"><AlertCircle size={14} /> {err}</div>}
      {okMsg && <div style={css.ok} data-testid="rt-ok"><CheckCircle2 size={14} /> {okMsg}</div>}

      <form onSubmit={save}>
        <Section title="🔔 التنبيه الموحّد (يظهر في البوابة + الفاتورة + السداد + السياسة)" theme={theme}>
          <Field label="النص (عربي)" theme={theme} full>
            <textarea rows={4} value={disclaimer.text_ar || ""} onChange={(e) => setDisc("text_ar", e.target.value)}
              style={{ ...css.input, resize: "vertical", lineHeight: 1.8 }} data-testid="rt-disclaimer-ar" />
          </Field>
          <Field label="Notice (English)" theme={theme} full>
            <textarea rows={4} value={disclaimer.text_en || ""} onChange={(e) => setDisc("text_en", e.target.value)}
              style={{ ...css.input, resize: "vertical", lineHeight: 1.8 }} data-testid="rt-disclaimer-en" />
          </Field>
        </Section>

        <Section title="رأس الإيصال" theme={theme}>
          <Field label="العنوان الرئيسي (عربي)" theme={theme}>
            <input value={form.header_ar || ""} onChange={(e) => set("header_ar", e.target.value)} style={css.input} data-testid="rt-header-ar" />
          </Field>
          <Field label="Main header (English)" theme={theme}>
            <input value={form.header_en || ""} onChange={(e) => set("header_en", e.target.value)} style={css.input} data-testid="rt-header-en" />
          </Field>
          <Field label="العنوان الفرعي (عربي)" theme={theme}>
            <input value={form.subheader_ar || ""} onChange={(e) => set("subheader_ar", e.target.value)} style={css.input} data-testid="rt-subheader-ar" />
          </Field>
          <Field label="Subheader (English)" theme={theme}>
            <input value={form.subheader_en || ""} onChange={(e) => set("subheader_en", e.target.value)} style={css.input} data-testid="rt-subheader-en" />
          </Field>
          <Field label="إظهار شعار المكتب" theme={theme}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={form.show_logo !== false} onChange={(e) => set("show_logo", e.target.checked)} data-testid="rt-show-logo" />
              يظهر اللوقو في رأس الإيصال
            </label>
          </Field>
        </Section>

        <Section title="معلومات المكتب (تظهر تحت الرأس)" theme={theme}>
          <Field label="السجل التجاري / الترخيص" theme={theme}>
            <input dir="ltr" value={form.license_number || ""} onChange={(e) => set("license_number", e.target.value)} style={{ ...css.input, fontFamily: "monospace" }} data-testid="rt-license" />
          </Field>
          <Field label="الرقم الضريبي" theme={theme}>
            <input dir="ltr" value={form.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} style={{ ...css.input, fontFamily: "monospace" }} data-testid="rt-vat" />
          </Field>
          <Field label="الهاتف" theme={theme}>
            <input dir="ltr" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} style={{ ...css.input, fontFamily: "monospace" }} data-testid="rt-phone" />
          </Field>
          <Field label="الموقع الإلكتروني" theme={theme}>
            <input dir="ltr" value={form.website || ""} onChange={(e) => set("website", e.target.value)} style={css.input} data-testid="rt-website" />
          </Field>
          <Field label="العنوان (عربي)" theme={theme} full>
            <input value={form.address_ar || ""} onChange={(e) => set("address_ar", e.target.value)} style={css.input} data-testid="rt-addr-ar" />
          </Field>
          <Field label="Address (English)" theme={theme} full>
            <input value={form.address_en || ""} onChange={(e) => set("address_en", e.target.value)} style={css.input} data-testid="rt-addr-en" />
          </Field>
        </Section>

        <Section title="ختم الإيصال" theme={theme}>
          <Field label="نص الختم (عربي)" theme={theme}>
            <input value={form.stamp_text_ar || ""} onChange={(e) => set("stamp_text_ar", e.target.value)} style={css.input} data-testid="rt-stamp-ar" />
          </Field>
          <Field label="Stamp text (English)" theme={theme}>
            <input value={form.stamp_text_en || ""} onChange={(e) => set("stamp_text_en", e.target.value)} style={css.input} data-testid="rt-stamp-en" />
          </Field>
        </Section>

        <Section title="ذيل الإيصال" theme={theme}>
          <Field label="نص الذيل (عربي)" theme={theme} full>
            <textarea rows={2} value={form.footer_ar || ""} onChange={(e) => set("footer_ar", e.target.value)}
              style={{ ...css.input, resize: "vertical" }} data-testid="rt-footer-ar" />
          </Field>
          <Field label="Footer text (English)" theme={theme} full>
            <textarea rows={2} value={form.footer_en || ""} onChange={(e) => set("footer_en", e.target.value)}
              style={{ ...css.input, resize: "vertical" }} data-testid="rt-footer-en" />
          </Field>
          <Field label="ملاحظة قانونية (عربي)" theme={theme} full>
            <textarea rows={2} value={form.legal_note_ar || ""} onChange={(e) => set("legal_note_ar", e.target.value)}
              style={{ ...css.input, resize: "vertical" }} data-testid="rt-legal-ar" />
          </Field>
          <Field label="Legal note (English)" theme={theme} full>
            <textarea rows={2} value={form.legal_note_en || ""} onChange={(e) => set("legal_note_en", e.target.value)}
              style={{ ...css.input, resize: "vertical" }} data-testid="rt-legal-en" />
          </Field>
        </Section>

        <div style={css.actions}>
          <button type="button" onClick={() => window.open("/portal/preview-receipt", "_blank")}
            style={css.previewBtn} data-testid="rt-preview-hint">
            <Eye size={14} strokeWidth={2.4} /> اختبر فاتورة عميل لمعاينة النتيجة
          </button>
          <button type="submit" disabled={busy} style={css.saveBtn} data-testid="rt-save">
            <Save size={14} strokeWidth={2.4} /> حفظ التغييرات
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children, theme }) {
  return (
    <section style={{ marginBottom: 16, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 14 }}>
      <h2 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: theme.gold }}>{title}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {children}
      </div>
    </section>
  );
}
function Field({ label, children, theme, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
function makeStyles(theme) {
  return {
    page: { padding: 18, background: theme.bg, color: theme.text, minHeight: "100vh" },
    head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" },
    title: { margin: 0, fontSize: 22, fontWeight: 900 },
    sub: { margin: 0, fontSize: 12, color: theme.textMuted },
    backBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    err: {
      background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: "10px 12px",
      borderRadius: 10, fontSize: 13, fontWeight: 700, marginBottom: 12,
      display: "inline-flex", gap: 8, alignItems: "center",
    },
    ok: {
      background: "rgba(22,163,74,0.10)", color: "#16a34a", padding: "10px 12px",
      borderRadius: 10, fontSize: 13, fontWeight: 800, marginBottom: 12,
      display: "inline-flex", gap: 8, alignItems: "center",
    },
    input: {
      width: "100%", padding: "8px 10px", fontSize: 13, fontFamily: "inherit",
      background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`,
      borderRadius: 8, outline: "none", boxSizing: "border-box",
    },
    actions: { display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8, flexWrap: "wrap" },
    previewBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    saveBtn: {
      background: theme.gold, color: "#0a0a0a", border: "none",
      padding: "10px 18px", borderRadius: 10, fontSize: 14, fontWeight: 900, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    muted: { color: theme.textMuted, padding: 8, fontSize: 13 },
  };
}
